import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { PermissionGuard } from "~/components/permission-guard";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { ensureSession } from "~/modules/auth/credentials";
import {
  migrateOrgByDefaultMutation,
  setOrgByDefaultMutation,
} from "~/modules/org-by-default/mutations";
import { orgByDefaultMigrationViewQuery } from "~/modules/org-by-default/queries";

export const Route = createFileRoute("/_dash/org-by-default/")({
  component: RouteComponent,
  beforeLoad() {
    ensureSession();
  },
});

function RouteComponent() {
  return (
    <PermissionGuard permission="users:edit">
      <OrgByDefaultPage />
    </PermissionGuard>
  );
}

const STORE_LABELS = {
  stripe: "Stripe",
  apple: "Apple",
  none: "None",
} as const;

const MIGRATE_OUTCOME_TOASTS = {
  migrated: {
    variant: "success",
    message: "Migrated to org-by-default",
  },
  already_migrated: {
    variant: "info",
    message: "User is already migrated",
  },
  skipped_active_subscription: {
    variant: "warning",
    message: "Skipped — active subscription (subscription transfer isn't supported yet)",
  },
} as const;

function getMigrateToastDescription(result: {
  outcome: "migrated" | "already_migrated" | "skipped_active_subscription";
  orgId?: string | null;
  reusedExistingOrg?: boolean | null;
}): string | undefined {
  if (result.outcome !== "migrated") {
    return;
  }
  const orgAction = result.reusedExistingOrg ? "Reused existing org" : "Created new org";
  return result.orgId ? `${orgAction} ${result.orgId}` : orgAction;
}

function getToggleButtonLabel(isOrgByDefault: boolean, isToggling: boolean): string {
  if (isOrgByDefault) {
    return isToggling ? "Removing..." : "Remove from cohort";
  }
  return isToggling ? "Enabling..." : "Add to cohort";
}

function OrgByDefaultPage() {
  const [userId, setUserId] = useState("");
  const [submittedUserId, setSubmittedUserId] = useState("");

  const { data, isFetching, isError, refetch } = orgByDefaultMigrationViewQuery.useQuery(
    { pathParams: { userId: submittedUserId } },
    { enabled: submittedUserId.length > 0 }
  );

  const { mutate: toggle, isPending: isToggling } = setOrgByDefaultMutation.useMutation({
    onSuccess(result) {
      if (result.success) {
        toast.success("Updated org-by-default status");
      } else {
        toast.error("Failed to update org-by-default status");
      }
    },
    onError() {
      toast.error("Failed to update org-by-default status");
    },
  });

  const { mutate: migrate, isPending: isMigrating } = migrateOrgByDefaultMutation.useMutation({
    onSuccess(result) {
      const outcomeToast = MIGRATE_OUTCOME_TOASTS[result.outcome];
      toast[outcomeToast.variant](outcomeToast.message, {
        description: getMigrateToastDescription(result),
      });
    },
    onError() {
      toast.error("Failed to migrate user");
    },
  });

  // Gate on the query's own state, not just the pending mutation: a settling refetch can still
  // serve pre-mutation `data`, and a failed refetch keeps the last successful `data`. Either way a
  // click would write against a stale cohort value. "Look up" stays enabled to retry.
  const canAct = !isToggling && !isMigrating && !isFetching && !isError;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = userId.trim();
    if (!trimmed) {
      return;
    }
    if (submittedUserId === trimmed) {
      refetch();
    } else {
      setSubmittedUserId(trimmed);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold">Org by Default Migration</h1>
      </div>

      <form onSubmit={handleSubmit} className="flex max-w-lg gap-2">
        <Input
          placeholder="Enter user ID"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
        />
        <Button type="submit" disabled={!userId.trim()}>
          Look up
        </Button>
      </form>

      {submittedUserId && (
        <div className="max-w-3xl space-y-4">
          {isFetching && !data && <p className="text-sm text-muted-foreground">Loading...</p>}
          {isError && <p className="text-sm text-destructive">User not found or request failed.</p>}
          {data && (
            <div className="space-y-6 rounded-md border p-4">
              <div>
                <p className="text-sm font-medium">User ID</p>
                <p className="font-mono text-sm text-muted-foreground">{submittedUserId}</p>
              </div>

              <div className="flex items-center justify-between gap-4 rounded-md border p-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">Org-by-Default Cohort</p>
                    <Badge variant={data.isOrgByDefault ? "default" : "outline"}>
                      {data.isOrgByDefault ? "In cohort" : "Not in cohort"}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Directly flips the cohort flag, without running the migration below.
                  </p>
                </div>
                <Button
                  variant={data.isOrgByDefault ? "destructive" : "outline"}
                  disabled={!canAct}
                  onClick={() =>
                    toggle({
                      pathParams: { userId: submittedUserId },
                      params: { enabled: !data.isOrgByDefault },
                    })
                  }
                >
                  {getToggleButtonLabel(data.isOrgByDefault, isToggling)}
                </Button>
              </div>

              <div className="flex items-center justify-between gap-4 rounded-md border p-4">
                <div>
                  <p className="text-sm font-medium">Migrate</p>
                  <p className="text-sm text-muted-foreground">
                    Runs org-creation and cohort-flag migration. Users with an active personal
                    subscription are skipped entirely (subscription transfer isn't supported yet).
                  </p>
                </div>
                <Button
                  disabled={!canAct}
                  onClick={() => migrate({ params: {}, pathParams: { userId: submittedUserId } })}
                >
                  {isMigrating ? "Migrating..." : "Migrate"}
                </Button>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">Organizations</p>
                {data.orgs.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No organizations found.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Org</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Store</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.orgs.map((org) => {
                        // A live Stripe/RevenueCat lookup failure, not a genuine no-subscription
                        // org — must not read as a benign "free" org when deciding to migrate.
                        const subscriptionLookupFailed = org.store === "unknown";
                        return (
                          <TableRow key={org.orgId}>
                            <TableCell>
                              <div className="font-medium">{org.orgName}</div>
                              <div className="font-mono text-xs text-muted-foreground">
                                {org.orgId}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary">{org.role}</Badge>
                            </TableCell>
                            <TableCell>{new Date(org.createdAt).toLocaleDateString()}</TableCell>
                            <TableCell>
                              {subscriptionLookupFailed ? (
                                <Badge variant="destructive">Lookup failed</Badge>
                              ) : (
                                org.planTier
                              )}
                            </TableCell>
                            <TableCell>
                              {org.store === "unknown" ? (
                                <Badge variant="destructive">Lookup failed</Badge>
                              ) : (
                                STORE_LABELS[org.store]
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
